// ============================================
// Recovery-hash snapshot
//
// Captures whether the page loaded with a Supabase password-recovery
// hash fragment, BEFORE any Supabase auth code has had a chance to
// process and clear the URL.
//
// Why this exists:
// - Supabase JS auto-detects the recovery token in `window.location.hash`
//   on initialize, exchanges it for a session, fires PASSWORD_RECOVERY,
//   then cleans the hash via history.replaceState.
// - That whole sequence runs from AuthProvider's first auth call, which
//   happens BEFORE the lazy-loaded ResetPasswordPage chunk mounts.
// - If ResetPasswordPage tries to subscribe to onAuthStateChange and
//   wait for PASSWORD_RECOVERY, it subscribes too late — Supabase doesn't
//   replay past events for new subscribers, so the form never unlocks
//   even on a legit recovery email.
//
// This module is imported eagerly from main.tsx, so the hash snapshot
// is taken at module evaluation time (synchronously, before React
// mounts and before AuthProvider runs). ResetPasswordPage then reads
// this constant as its primary "is recovery flow" signal.
//
// Format reference (Supabase implicit flow):
//   #access_token=<jwt>&expires_in=3600&refresh_token=<rt>&token_type=bearer&type=recovery
// ============================================

// A genuine recovery hash carries BOTH a recovery marker AND an actual
// access token. Requiring the token is a security gate, not cosmetics:
// ResetPasswordPage unlocks its password form when this is true, and
// updateUser({password}) rewrites whatever session is currently active.
// A presence-only check on `type=recovery` let anyone unlock the form
// with a hand-typed `#type=recovery` URL - on a shared/kiosk device
// with a victim still signed in, that rewrites the victim's password
// (account takeover). The access_token requirement kills that: a real
// recovery link always has one; a hand-crafted bypass URL does not.
// (CSO audit 2026-07-07, finding #1.)
export function isRecoveryHash(hash: string): boolean {
  return /(?:^|[#&])type=recovery(?:&|$)/.test(hash) &&
    /(?:^|[#&])access_token=[^&]/.test(hash);
}

export const HAD_RECOVERY_HASH: boolean =
  typeof window !== 'undefined' && isRecoveryHash(window.location.hash);
